import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Download,
} from 'lucide-react';

interface ConversionData {
  id: string;
  call_sid: string;
  gclid: string;
  job_id: string | null;
  conversion_value: number | null;
  conversion_time: string;
  status: string;
  upload_attempts: number;
  uploaded_at: string | null;
  error_message: string | null;
  created_at: string;
  marketing_call_analytics: {
    customer_name: string;
    customer_phone: string;
    company_id: string;
  };
}

interface UploadLog {
  id: string;
  uploaded_at: string;
  conversions_count: number;
  successful_count: number;
  failed_count: number;
  upload_metadata: any;
}

interface Stats {
  totalConversions: number;
  uploaded: number;
  pending: number;
  failed: number;
  totalValue: number;
  avgTimeToUpload: number;
}

export default function ConversionAttributionPage() {
  const [conversions, setConversions] = useState<ConversionData[]>([]);
  const [uploadLogs, setUploadLogs] = useState<UploadLog[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalConversions: 0,
    uploaded: 0,
    pending: 0,
    failed: 0,
    totalValue: 0,
    avgTimeToUpload: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<number>(30);

  useEffect(() => {
    loadData();
  }, [statusFilter, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      // Build query
      let query = supabase
        .from('google_ads_conversion_queue')
        .select(`
          *,
          marketing_call_analytics!inner(
            customer_name,
            customer_phone,
            company_id
          )
        `)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(100);

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data: conversionsData, error: conversionsError } = await query;

      if (conversionsError) throw conversionsError;

      setConversions(conversionsData || []);

      // Calculate stats
      if (conversionsData) {
        const stats: Stats = {
          totalConversions: conversionsData.length,
          uploaded: conversionsData.filter(c => c.status === 'uploaded').length,
          pending: conversionsData.filter(c => c.status === 'pending').length,
          failed: conversionsData.filter(c => c.status === 'failed').length,
          totalValue: conversionsData.reduce((sum, c) => sum + (c.conversion_value || 0), 0),
          avgTimeToUpload: 0,
        };

        // Calculate average time to upload for completed conversions
        const uploadedConversions = conversionsData.filter(
          c => c.status === 'uploaded' && c.uploaded_at
        );
        if (uploadedConversions.length > 0) {
          const totalTime = uploadedConversions.reduce((sum, c) => {
            const created = new Date(c.created_at).getTime();
            const uploaded = new Date(c.uploaded_at!).getTime();
            return sum + (uploaded - created);
          }, 0);
          stats.avgTimeToUpload = totalTime / uploadedConversions.length / (1000 * 60); // Convert to minutes
        }

        setStats(stats);
      }

      // Load upload logs
      const { data: logsData, error: logsError } = await supabase
        .from('google_ads_conversion_uploads')
        .select('*')
        .gte('uploaded_at', startDate.toISOString())
        .order('uploaded_at', { ascending: false })
        .limit(20);

      if (logsError) throw logsError;

      setUploadLogs(logsData || []);
    } catch (error) {
      console.error('Error loading conversion data:', error);
    } finally {
      setLoading(false);
    }
  };

  const retryFailedConversions = async () => {
    try {
      const { error } = await supabase
        .from('google_ads_conversion_queue')
        .update({ status: 'pending', upload_attempts: 0, error_message: null })
        .eq('status', 'failed');

      if (error) throw error;

      alert('Failed conversions have been reset to pending status. They will be retried on the next sync.');
      loadData();
    } catch (error) {
      console.error('Error retrying conversions:', error);
      alert('Error retrying conversions');
    }
  };

  const exportData = () => {
    const csv = [
      ['Date', 'GCLID', 'Customer', 'Phone', 'Job ID', 'Value', 'Status', 'Uploaded At', 'Error'].join(','),
      ...conversions.map(c =>
        [
          new Date(c.created_at).toLocaleDateString(),
          c.gclid,
          c.marketing_call_analytics.customer_name,
          c.marketing_call_analytics.customer_phone,
          c.job_id || '',
          c.conversion_value || 0,
          c.status,
          c.uploaded_at ? new Date(c.uploaded_at).toLocaleString() : '',
          c.error_message || '',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'uploaded':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading conversion data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Ads Conversion Attribution</h1>
          <p className="text-gray-600 mt-1">
            Track offline conversions sent to Google Ads
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={exportData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          {stats.failed > 0 && (
            <button
              onClick={retryFailedConversions}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Failed ({stats.failed})
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Conversions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalConversions}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Uploaded</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.uploaded}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.failed}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${stats.totalValue.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="uploaded">Uploaded</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {stats.avgTimeToUpload > 0 && (
            <div className="ml-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avg Upload Time
              </label>
              <p className="text-lg font-semibold text-gray-900">
                {Math.round(stats.avgTimeToUpload)} min
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Conversions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Conversions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  GCLID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Job ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Attempts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {conversions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No conversions found for the selected filters
                  </td>
                </tr>
              ) : (
                conversions.map((conversion) => (
                  <tr key={conversion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(conversion.status)}
                        <span className={`text-sm font-medium ${getStatusColor(conversion.status)}`}>
                          {conversion.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {conversion.marketing_call_analytics.customer_name}
                        </div>
                        <div className="text-gray-500">
                          {conversion.marketing_call_analytics.customer_phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900 font-mono">
                        {conversion.gclid.substring(0, 12)}...
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {conversion.job_id || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        ${conversion.conversion_value?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500">
                        {new Date(conversion.created_at).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500">
                        {conversion.uploaded_at
                          ? new Date(conversion.uploaded_at).toLocaleString()
                          : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {conversion.upload_attempts}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Logs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Upload History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Upload Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Successful
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Failed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Success Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {uploadLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No upload history available
                  </td>
                </tr>
              ) : (
                uploadLogs.map((log) => {
                  const successRate = log.conversions_count > 0
                    ? (log.successful_count / log.conversions_count) * 100
                    : 0;

                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {new Date(log.uploaded_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {log.conversions_count}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-green-600">
                          {log.successful_count}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-red-600">
                          {log.failed_count}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                successRate === 100 ? 'bg-green-600' :
                                successRate >= 75 ? 'bg-yellow-600' :
                                'bg-red-600'
                              }`}
                              style={{ width: `${successRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-900 w-12 text-right">
                            {successRate.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
